"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.createFilter = exports.isDateRangeFilter = exports.isTextFilter = exports.isRelativeDateFilter = exports.isCascadingFilter = exports.isLogicalAttributeFilter = exports.isExcludeFilter = exports.isMeasureFilter = exports.isRankingFilter = exports.isNumericFilter = exports.isMembersFilter = exports.isCustomFilter = exports.CustomFilter = exports.RelativeDateFilter = exports.DateRangeFilter = exports.TextFilter = exports.NumericFilter = exports.RankingFilter = exports.MeasureFilter = exports.DoubleOperatorFilter = exports.ExcludeFilter = exports.CascadingFilter = exports.MembersFilter = exports.LogicalAttributeFilter = exports.FilterTypes = exports.RankingOperators = exports.LogicalOperators = exports.DateOperators = exports.NumericOperators = exports.TextOperators = void 0;
const hash_it_1 = __importDefault(require("hash-it"));
const base_js_1 = require("../base.js");
const types_js_1 = require("../types.js");
const factory_js_1 = require("../factory.js");
const measures_js_1 = require("../measures/measures.js");
const translatable_error_js_1 = require("../../translation/translatable-error.js");
const filter_config_utils_js_1 = require("./filter-config-utils.js");
const merge_js_1 = __importDefault(require("lodash-es/merge.js"));
const omit_js_1 = __importDefault(require("lodash-es/omit.js"));
/**
 * Different text operators that can be used with text filters
 *
 * @internal
 */
exports.TextOperators = {
    Contains: 'contains',
    StartsWith: 'startsWith',
    EndsWith: 'endsWith',
    Equals: 'equals',
    DoesntEqual: 'doesntEqual',
    DoesntStartWith: 'doesntStartWith',
    DoesntContain: 'doesntContain',
    DoesntEndWith: 'doesntEndWith',
    Like: 'like',
};
/**
 * Different numeric operators that can be used with numeric filters
 */
exports.NumericOperators = {
    Equals: 'equals',
    DoesntEqual: 'doesntEqual',
    From: 'from',
    FromNotEqual: 'fromNotEqual',
    To: 'to',
    ToNotEqual: 'toNotEqual',
};
/**
 * Different date operators that can be used with date filters
 *
 * @internal
 */
exports.DateOperators = {
    From: 'from',
    To: 'to',
    Last: 'last',
    Next: 'next',
    Anchor: 'Anchor',
};
/**
 * Different logical operators that can be used with logical filters
 *
 * @internal
 */
exports.LogicalOperators = {
    Union: 'or',
    Intersection: 'and',
    Exclude: 'exclude',
};
/**
 * Different ranking operators that can be used with ranking filter
 *
 * @internal
 */
exports.RankingOperators = {
    Top: 'top',
    Bottom: 'bottom',
};
/**
 * Different filter types
 *
 * @internal
 */
exports.FilterTypes = {
    logicalAttribute: 'logicalAttribute',
    members: 'members',
    exclude: 'exclude',
    measure: 'measure',
    ranking: 'ranking',
    text: 'text',
    numeric: 'numeric',
    dateRange: 'dateRange',
    relativeDate: 'relativeDate',
    cascading: 'cascading',
    advanced: 'advanced',
};
// CLASSES
/**
 * base implementation for filter classes
 *
 * @internal
 */
class AbstractFilter extends base_js_1.DimensionalElement {
    constructor(att, filterType, config) {
        super('filter', types_js_1.MetadataTypes.Filter);
        this.filterType = filterType;
        // need to set isScope
        // to make filter complete for comparison
        this.isScope = true;
        AbstractFilter.checkAttributeSupport(att);
        this.attribute = att;
        this.config = (0, merge_js_1.default)({}, (0, filter_config_utils_js_1.getDefaultBaseFilterConfig)(), config !== null && config !== void 0 ? config : {});
    }
    get name() {
        // to hexadecimal string
        return (0, hash_it_1.default)(this.jaql()).toString(16);
    }
    /**
     * Gets a serializable representation of the element
     */
    serializable() {
        const result = super.serializable();
        result.filterType = this.filterType;
        result.attribute = this.attribute.serializable();
        return result;
    }
    /**
     * Gets the JAQL representation of this instance
     *
     * @param nested - defines whether the JAQL is nested within parent JAQL statement or a root JAQL element
     */
    jaql(nested) {
        if (this.config.disabled) {
            return AbstractFilter.disabledJaql(nested);
        }
        const result = this.attribute.jaql(false);
        const level = this.attribute;
        if (level.getFormat && level.getFormat() !== undefined) {
            const granularityJaql = level.translateGranularityToJaql();
            result.format = {
                mask: {
                    [granularityJaql.level]: level.getFormat(),
                },
            };
        }
        result.jaql.filter = this.filterJaql();
        // prioritize attribute dataSource for the use case of multi-source dashboard
        if (this.attribute.dataSource) {
            result.jaql.datasource = this.attribute.dataSource;
        }
        if (this.isScope) {
            result.panel = 'scope';
        }
        return nested === true ? result.jaql : result;
    }
    static checkAttributeSupport(attribute) {
        const { granularity } = attribute;
        if (granularity === types_js_1.DateLevels.Hours ||
            granularity === types_js_1.DateLevels.MinutesRoundTo30 ||
            granularity === types_js_1.DateLevels.MinutesRoundTo15 ||
            granularity === types_js_1.DateLevels.Minutes ||
            granularity === types_js_1.DateLevels.Seconds) {
            throw new translatable_error_js_1.TranslatableError('errors.filter.unsupportedDatetimeLevel');
        }
    }
    static disabledJaql(nested) {
        return nested ? { filter: {} } : { jaql: { filter: {} } };
    }
}
/**
 * @internal
 */
class LogicalAttributeFilter extends AbstractFilter {
    constructor(filters, operator, config) {
        super(filters[0].attribute, exports.FilterTypes.logicalAttribute, config);
        this.operator = operator;
        this.filters = filters;
    }
    /**
     * gets the element's ID
     */
    get id() {
        return `${this.operator}_${this.filters.map((f) => f.id).join()}`;
    }
    /**
     * Gets a serializable representation of the element
     */
    serializable() {
        const result = super.serializable();
        result.operator = this.operator;
        result.filters = this.filters.map((f) => f.serializable());
        return result;
    }
    /**
     * Gets JAQL representing this Filter instance
     */
    filterJaql() {
        const result = {};
        result[this.operator] = this.filters.map((f) => f.filterJaql());
        return result;
    }
}
exports.LogicalAttributeFilter = LogicalAttributeFilter;
/**
 * @internal
 */
class MembersFilter extends AbstractFilter {
    constructor(attribute, members, config) {
        super(attribute, exports.FilterTypes.members);
        this.members = members !== null && members !== void 0 ? members : [];
        if (this.members.filter((m) => m === null || m === undefined).length > 0) {
            throw new translatable_error_js_1.TranslatableError('errors.filter.membersFilterNullMember', {
                attributeId: attribute.id,
            });
        }
        // merge default config and input config into a new object
        // to avoid mutation
        this.config = (0, merge_js_1.default)({}, (0, filter_config_utils_js_1.getDefaultMembersFilterConfig)(), config !== null && config !== void 0 ? config : {});
    }
    /**
     * gets the element's ID
     */
    get id() {
        return `${this.attribute.id}_${this.members.map((m) => m.toString()).join()}`;
    }
    get name() {
        // to hexadecimal string
        return (0, hash_it_1.default)([this.jaql(), (0, omit_js_1.default)(this.config, ['guid', 'originalFilterJaql'])]).toString(16);
    }
    /**
     * Gets a serializable representation of the element
     */
    serializable() {
        const result = super.serializable();
        result.members = this.members;
        return result;
    }
    /**
     * Gets JAQL representing this Filter instance
     */
    filterJaql() {
        const membersFilterJaql = {
            members: this.members.map((m) => m.toString()),
        };
        const filterJaql = this.config.excludeMembers
            ? { exclude: membersFilterJaql }
            : membersFilterJaql;
        if (this.config.backgroundFilter) {
            return {
                and: [filterJaql, this.config.backgroundFilter.filterJaql()],
            };
        }
        return filterJaql;
    }
}
exports.MembersFilter = MembersFilter;
/**
 * @internal
 */
class CascadingFilter extends AbstractFilter {
    constructor(filters, config) {
        super(filters[0].attribute, exports.FilterTypes.cascading, config);
        this._filters = filters;
    }
    /**
     * Returns the level filters with the root config applied.
     */
    get filters() {
        const { disabled, locked } = this.config;
        return this._filters.map((filter) => {
            filter.config = Object.assign(Object.assign({}, filter.config), { disabled, locked });
            return filter;
        });
    }
    /**
     * gets the element's ID
     */
    get id() {
        return `${this.filterType}_${this._filters.map((f) => f.id).join()}`;
    }
    /**
     * Gets a serializable representation of the element
     */
    serializable() {
        const result = super.serializable();
        result.filters = this.filters.map((f) => f.serializable());
        return result;
    }
    /**
     * Gets JAQL representing this Filter instance
     */
    filterJaql() {
        // return empty object as jaql is handled by jaql() method
        return {};
    }
    jaql(nested) {
        if (this.config.disabled) {
            return AbstractFilter.disabledJaql(nested);
        }
        // return jaql of all level filters treated as scope filters
        return this.filters.map((f) => {
            f.isScope = true;
            return f.jaql(nested);
        });
    }
}
exports.CascadingFilter = CascadingFilter;
/**
 * @internal
 */
class ExcludeFilter extends AbstractFilter {
    constructor(filter, input, config) {
        super(filter.attribute, exports.FilterTypes.exclude, config);
        this.input = input;
        this.filter = filter;
    }
    /**
     * gets the element's ID
     */
    get id() {
        let result = `exclude_${this.filter.id}`;
        if (this.input) {
            result += '_from_' + this.input.id;
        }
        return result;
    }
    /**
     * Gets a serializable representation of the element
     */
    serializable() {
        const result = super.serializable();
        result.filter = this.filter.serializable();
        if (this.input) {
            result.input = this.input.serializable();
        }
        return result;
    }
    /**
     * Gets JAQL representing this Filter instance
     */
    filterJaql() {
        const result = {};
        const exclusion = this.filter.filterJaql();
        if (this.input) {
            result.filter = this.input.filterJaql();
            result.filter.filter = { exclude: exclusion };
        }
        else {
            result.exclude = exclusion;
        }
        return result;
    }
}
exports.ExcludeFilter = ExcludeFilter;
/**
 * @internal
 */
class DoubleOperatorFilter extends AbstractFilter {
    constructor(att, filterType, operatorA, valueA, operatorB, valueB, config) {
        super(att, filterType, config);
        if (operatorA && valueA !== undefined) {
            this.valueA = valueA;
            this.operatorA = operatorA;
        }
        if (operatorB && valueB !== undefined) {
            this.operatorB = operatorB;
            this.valueB = valueB;
        }
    }
    /**
     * gets the element's ID
     */
    get id() {
        let result = `${this.attribute.id}`;
        if (this.operatorA && this.valueA !== undefined) {
            result += `_${this.operatorA}:${this.valueA}`;
        }
        if (this.operatorB && this.valueB !== undefined) {
            result += `_${this.operatorB}:${this.valueB}`;
        }
        return result;
    }
    /**
     * Gets a serializable representation of the element
     */
    serializable() {
        const result = super.serializable();
        if (this.operatorA) {
            result.operatorA = this.operatorA;
        }
        if (this.operatorB) {
            result.operatorB = this.operatorB;
        }
        if (this.valueA !== undefined) {
            result.valueA = this.valueA;
        }
        if (this.valueB !== undefined) {
            result.valueB = this.valueB;
        }
        return result;
    }
    /**
     * Gets JAQL representing this Filter instance
     */
    filterJaql() {
        const result = {};
        if (this.operatorA && this.valueA !== undefined) {
            result[this.operatorA] = this.valueA;
        }
        if (this.operatorB && this.valueB !== undefined) {
            result[this.operatorB] = this.valueB;
        }
        return result;
    }
}
exports.DoubleOperatorFilter = DoubleOperatorFilter;
/**
 * @internal
 */
class MeasureFilter extends DoubleOperatorFilter {
    constructor(att, measure, operatorA, valueA, operatorB, valueB, config) {
        super(att, exports.FilterTypes.measure, operatorA, valueA, operatorB, valueB, config);
        this.measure = measure;
    }
    /**
     * gets the element's ID
     */
    get id() {
        let result = `${this.attribute.id}_${this.measure.id}`;
        if (this.operatorA && this.valueA !== undefined) {
            result += `_${this.operatorA}:${this.valueA}`;
        }
        if (this.operatorB && this.valueB !== undefined) {
            result += `_${this.operatorB}${this.valueB}`;
        }
        return result;
    }
    /**
     * Gets a serializable representation of the element
     */
    serializable() {
        const result = super.serializable();
        result.measure = this.measure.serializable();
        return result;
    }
    jaql(nested) {
        if (this.config.disabled) {
            return AbstractFilter.disabledJaql(nested);
        }
        const result = super.jaql(nested);
        if (this.measure instanceof measures_js_1.DimensionalBaseMeasure) {
            Object.entries(this.measure.jaql().jaql).forEach(([key, value]) => {
                result.jaql[key] = value;
            });
        }
        return result;
    }
}
exports.MeasureFilter = MeasureFilter;
/**
 * @internal
 */
class RankingFilter extends AbstractFilter {
    constructor(att, measure, operator, count, config) {
        super(att, exports.FilterTypes.ranking, config);
        this.count = count;
        this.operator = operator;
        this.measure = measure;
    }
    /**
     * gets the element's ID
     */
    get id() {
        return `${this.operator}_${this.count}_${this.attribute.id}_by_${this.measure.id}`;
    }
    /**
     * Gets a serializable representation of the element
     */
    serializable() {
        const result = super.serializable();
        result.count = this.count;
        result.operator = this.operator;
        result.measure = this.measure.serializable();
        return result;
    }
    /**
     * Gets JAQL representing this Filter instance
     */
    filterJaql() {
        const result = {};
        result[this.operator] = this.count;
        result.by = this.measure.jaql(true);
        return result;
    }
}
exports.RankingFilter = RankingFilter;
/**
 * @internal
 */
class NumericFilter extends DoubleOperatorFilter {
    constructor(att, operatorA, valueA, operatorB, valueB, config) {
        super(att, exports.FilterTypes.numeric, operatorA, valueA, operatorB, valueB, config);
    }
}
exports.NumericFilter = NumericFilter;
/**
 * @internal
 */
class TextFilter extends DoubleOperatorFilter {
    constructor(att, operator, value, config) {
        super(att, exports.FilterTypes.text, operator, value, undefined, undefined, config);
    }
}
exports.TextFilter = TextFilter;
/**
 * @internal
 */
class DateRangeFilter extends DoubleOperatorFilter {
    constructor(l, valueFrom, valueTo, config) {
        super(l, exports.FilterTypes.dateRange, exports.DateOperators.From, valueFrom, exports.DateOperators.To, valueTo, config);
        if (typeof valueFrom === 'object') {
            this.valueA = valueFrom.toISOString();
        }
        if (typeof valueTo === 'object') {
            this.valueB = valueTo.toISOString();
        }
    }
    get level() {
        return this.attribute;
    }
    get from() {
        return this.valueA;
    }
    get to() {
        return this.valueB;
    }
    /**
     * Gets JAQL representing this Filter instance
     */
    filterJaql() {
        return super.filterJaql();
    }
}
exports.DateRangeFilter = DateRangeFilter;
/**
 * @internal
 */
class RelativeDateFilter extends AbstractFilter {
    constructor(l, offset, count, operator, anchor, config) {
        super(l, exports.FilterTypes.relativeDate, config);
        if (!operator) {
            operator = exports.DateOperators.Next;
        }
        this.anchor = anchor;
        this.operator = operator;
        this.offset = offset;
        this.count = count;
    }
    get level() {
        return this.attribute;
    }
    /**
     * gets the element's ID
     */
    get id() {
        let result = `${this.level.id}_${this.operator}_${this.offset}_${this.count}`;
        if (this.anchor) {
            if (typeof this.anchor === 'string') {
                result += `_${this.anchor}`;
            }
            else if (typeof this.anchor === 'object') {
                result += `_${this.anchor.toISOString()}`;
            }
        }
        return result;
    }
    /**
     * Gets a serializable representation of the element
     */
    serializable() {
        const result = super.serializable();
        result.offset = this.offset;
        result.count = this.count;
        result.operator = this.operator;
        if (this.anchor) {
            result.anchor = this.anchor;
        }
        return result;
    }
    /**
     * Gets JAQL representing this Filter instance
     *
     */
    filterJaql() {
        const result = {};
        result[this.operator] = {
            offset: this.offset,
            count: this.count,
        };
        if (this.anchor) {
            if (typeof this.anchor === 'string') {
                result[this.operator].anchor = this.anchor;
            }
            else if (typeof this.anchor === 'object') {
                result[this.operator].anchor = this.anchor.toISOString();
            }
        }
        return result;
    }
}
exports.RelativeDateFilter = RelativeDateFilter;
/**
 * @internal
 */
class CustomFilter extends AbstractFilter {
    constructor(att, jaql, config) {
        super(att, exports.FilterTypes.advanced, config);
        // remove filterType from jaql as it is not needed
        delete jaql.filterType;
        this.jaqlExpression = jaql;
    }
    /**
     * gets the element's ID
     */
    get id() {
        return `custom_${this.attribute.id}_${this.config.guid}`;
    }
    /**
     * Gets JAQL representing this Filter instance
     *
     */
    filterJaql() {
        return this.jaqlExpression;
    }
}
exports.CustomFilter = CustomFilter;
/**
 * Checks if a filter is a CustomFilter.
 *
 * @param filter - The filter to check.
 * @internal
 */
function isCustomFilter(filter) {
    return 'filterType' in filter && filter.filterType === exports.FilterTypes.advanced;
}
exports.isCustomFilter = isCustomFilter;
/**
 * Checks if a filter is a MembersFilter.
 *
 * @param filter - The filter to check.
 * @internal
 */
function isMembersFilter(filter) {
    return 'filterType' in filter && filter.filterType === exports.FilterTypes.members;
}
exports.isMembersFilter = isMembersFilter;
/**
 * Checks if a filter is a NumericFilter.
 *
 * @param filter - The filter to check.
 * @internal
 */
function isNumericFilter(filter) {
    return 'filterType' in filter && filter.filterType === exports.FilterTypes.numeric;
}
exports.isNumericFilter = isNumericFilter;
/**
 * Checks if a filter is a TextFilter.
 *
 * @param filter - The filter to check.
 * @internal
 */
function isRankingFilter(filter) {
    return 'filterType' in filter && filter.filterType === exports.FilterTypes.ranking;
}
exports.isRankingFilter = isRankingFilter;
/**
 * Checks if a filter is a MeasureFilter.
 *
 * @param filter - The filter to check.
 * @internal
 */
function isMeasureFilter(filter) {
    return 'filterType' in filter && filter.filterType === exports.FilterTypes.measure;
}
exports.isMeasureFilter = isMeasureFilter;
/**
 * Checks if a filter is a ExcludeFilter.
 *
 * @param filter - The filter to check.
 * @internal
 */
function isExcludeFilter(filter) {
    return 'filterType' in filter && filter.filterType === exports.FilterTypes.exclude;
}
exports.isExcludeFilter = isExcludeFilter;
/**
 * Checks if a filter is a LogicalAttributeFilter.
 *
 * @param filter - The filter to check.
 * @internal
 */
function isLogicalAttributeFilter(filter) {
    return 'filterType' in filter && filter.filterType === exports.FilterTypes.logicalAttribute;
}
exports.isLogicalAttributeFilter = isLogicalAttributeFilter;
/**
 * Checks if a filter is a CascadingFilter.
 *
 * @param filter - The filter to check.
 * @internal
 */
function isCascadingFilter(filter) {
    return 'filterType' in filter && filter.filterType === exports.FilterTypes.cascading;
}
exports.isCascadingFilter = isCascadingFilter;
/**
 * Checks if a filter is a RelativeDateFilter.
 *
 * @param filter - The filter to check.
 * @internal
 */
function isRelativeDateFilter(filter) {
    return 'filterType' in filter && filter.filterType === exports.FilterTypes.relativeDate;
}
exports.isRelativeDateFilter = isRelativeDateFilter;
/**
 * Checks if a filter is a TextFilter.
 *
 * @param filter - The filter to check.
 * @internal
 */
function isTextFilter(filter) {
    return 'filterType' in filter && filter.filterType === exports.FilterTypes.text;
}
exports.isTextFilter = isTextFilter;
/**
 * Checks if a filter is a DateRangeFilter.
 *
 * @param filter - The filter to check.
 * @internal
 */
function isDateRangeFilter(filter) {
    return 'filterType' in filter && filter.filterType === exports.FilterTypes.dateRange;
}
exports.isDateRangeFilter = isDateRangeFilter;
/**
 * @param json - Filter JSON representation
 * @internal
 */
function createFilter(json) {
    switch (json.filterType) {
        case exports.FilterTypes.logicalAttribute:
            return new LogicalAttributeFilter(json.filters.map((f) => createFilter(f)), json.operator);
            break;
        case exports.FilterTypes.members:
            return new MembersFilter((0, factory_js_1.create)(json.attribute), json.members);
            break;
        case exports.FilterTypes.exclude:
            return new ExcludeFilter(createFilter(json.filter), json.input && createFilter(json.input));
            break;
        case exports.FilterTypes.measure:
            return new MeasureFilter((0, factory_js_1.create)(json.attribute), (0, factory_js_1.create)(json.measure), json.operatorA, json.valueA, json.operatorB, json.valueB);
            break;
        case exports.FilterTypes.ranking:
            return new RankingFilter((0, factory_js_1.create)(json.attribute), (0, factory_js_1.create)(json.measure), json.operator, json.count);
            break;
        case exports.FilterTypes.numeric:
            return new NumericFilter((0, factory_js_1.create)(json.attribute), json.operatorA, json.valueA, json.operatorB, json.valueB);
            break;
        case exports.FilterTypes.text:
            return new TextFilter((0, factory_js_1.create)(json.attribute), json.operatorA, json.valueA);
            break;
        case exports.FilterTypes.relativeDate:
            return new RelativeDateFilter((0, factory_js_1.create)(json.attribute), json.offset, json.count, json.operator, json.anchor);
            break;
        case exports.FilterTypes.dateRange:
            return new DateRangeFilter((0, factory_js_1.create)(json.attribute), json.valueA, json.valueB);
            break;
    }
    throw new translatable_error_js_1.TranslatableError('errors.filter.unsupportedType', {
        filterType: json.filterType,
    });
}
exports.createFilter = createFilter;
